"use client";

import {
  FC,
  useEffect,
  useMemo,
  useState,
} from "react";
import dayjs from "dayjs";
import { useRouter, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCookies } from "react-cookie";

import { Theme } from "apps/website/maps/Theme.map";
import Section from "apps/website/components/layout/Section/Section";
import Container from "apps/website/components/layout/Container/Container";
import Spacer from "apps/website/components/layout/Spacer/Spacer";
import Grid from "apps/website/components/layout/Grid/Grid";
import Column from "apps/website/components/layout/Column/Column";
import ImageCarousel from "apps/website/components/feature/Carousel/ImageCarousel/ImageCarousel";
import Button from "apps/website/components/base/Button/Button";
import { DisplayState } from "@/constants/state";
import { FormsActionsAPI } from "apps/website/api/FormsActions";
import { PublicConfig } from "apps/website/config/public";
import { KatKinProduct } from "@forms/schema";
import {
  getFlowFieldValuesForAction,
} from "libs/state/src/lib/stores/useFormServiceStore";
import {
  IPossibleDeliveryDate,
  IPossibleDeliveryDates,
} from "libs/api/customer/src/lib/types/delivery-dates";
import { useAuth, useDatadog } from "@auth/client-sdk-react";
import { useQueryParams } from "apps/website/hooks/useQueryParams";

import {
  IOneClickPurchaseCard,
} from "../SectionOneClickCard/SectionOneClickCard";
import Image from "../../base/Image/Image";
import {
  getActionTrackingInfo,
} from "../../../utils/tracking/getActionTrackingInfo";
import TextBody from "../../base/Text/TextBody/TextBody";
import Select from "../../form/Select/Select";
import SimpleTag from "../../base/Tag/v2/SimpleTag/SimpleTag";
import TextSubtitle from "../../base/Text/TextSubtitle/TextSubtitle";

export interface ISectionOneClickPurchase {
  title?: string;
  card: IOneClickPurchaseCard;
  continueToPath: string;
  noThanksPath?: string;
  theme?: Theme;
  product?: KatKinProduct;
  params?: Record<string, string>;
}

const DATE_FORMAT = "YYYY-MM-DD";

const SectionOneClickPurchaseWithDate: FC<ISectionOneClickPurchase> = ({ title, card, theme = "light-grey", continueToPath, noThanksPath, params, product }) => {

  const { accessToken } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const { chainQueryParamsToUrl } = useQueryParams();
  const posthog = usePostHog();
  const datadog = useDatadog();
  const [ cookies ] = useCookies([ "_fbp", "_fbc" ]);

  const formsActionsAPI = useMemo(() => new FormsActionsAPI(
    PublicConfig.NEXT_PUBLIC_FORM_ACTIONS_URL,
    accessToken,
  ), [ accessToken ]);

  const [ deliveryDate, setDeliveryDate ] = useState<string | null>(null);
  const [ checkoutState, setCheckoutState ] = useState<DisplayState>(DisplayState.READY);
  const [ possibleDeliveryDates, setPossibleDeliveryDates ] = useState<IPossibleDeliveryDate[]>([]);

  const checkoutWithProductAndContinue = async () => {
    setCheckoutState(DisplayState.PROCESSING);

    try {
      const featureFlags = posthog.featureFlags.getFlagVariants();
      const fieldSubmitValuesMap = getFlowFieldValuesForAction("");
      if (params?.discountCode) {
        fieldSubmitValuesMap.set("discountCode", { value: params.discountCode });
      }
      if (params?.cadence) {
        fieldSubmitValuesMap.set("cadence", { value: params.cadence });
      }
      if (params?.quantity) {
        fieldSubmitValuesMap.set("quantity", { value: params.quantity });
      }
      if (deliveryDate) {
        fieldSubmitValuesMap.set("deliveryDate", { value: deliveryDate });
      }
      const result = await formsActionsAPI.performAction<never>(
        "checkoutLitter",
        fieldSubmitValuesMap,
        product ?? "UNKNOWN",
        window.location.pathname,
        undefined,
        featureFlags,
        getActionTrackingInfo(cookies),
        datadog.logger,
      );
      if (result.success) {

        void router.push(chainQueryParamsToUrl(continueToPath, {
          allCatNames: searchParams.get("allCatNames") ?? undefined,
          allCatGenders: searchParams.get("allCatGenders") ?? undefined,
          deliveryDate: searchParams.get("deliveryDate") ? dayjs(searchParams.get("deliveryDate") as string).format(DATE_FORMAT) : undefined,
          litterDeliveryDate: dayjs(deliveryDate).format(DATE_FORMAT),
          katkinCustomerId: searchParams.get("katkinCustomerId") ?? undefined,
          signUpRoute: searchParams.get("signUpRoute") ?? undefined,
          flow: searchParams.get("flow") ?? undefined,
        }));
        setCheckoutState(DisplayState.COMPLETE);
      }
    } catch {
      console.error("Couldn't checkout");
    }
    setCheckoutState(DisplayState.ERROR);
  };

  const continueWithoutProduct = async () => {
    void router.push(chainQueryParamsToUrl(noThanksPath || continueToPath, {
      allCatNames: searchParams.get("allCatNames") ?? undefined,
      allCatGenders: searchParams.get("allCatGenders") ?? undefined,
      deliveryDate: searchParams.get("deliveryDate") ? dayjs(searchParams.get("deliveryDate") as string).format(DATE_FORMAT) : undefined,
      katkinCustomerId: searchParams.get("katkinCustomerId") ?? undefined,
      signUpRoute: searchParams.get("signUpRoute") ?? undefined,
      flow: searchParams.get("flow") ?? undefined,
    }));
  };

  const getNextAvailableDeliveryDate = async (): Promise<void> => {
    try {
      const featureFlags = posthog.featureFlags.getFlagVariants();
      const deliveryDatesResponse = await formsActionsAPI.performAction<IPossibleDeliveryDates>(
        "getAvailableLitterTrialDeliveryDates",
        new Map(),
        product ?? "UNKNOWN",
        undefined,
        undefined,
        featureFlags,
        getActionTrackingInfo(cookies),
        datadog.logger,
      );
      if (deliveryDatesResponse.success) {
        if (searchParams.get("deliveryDate") === "string") {
          const queryParamDate = dayjs(searchParams.get("deliveryDate"));
          const possDeliveryDates = deliveryDatesResponse.responseBody;
          const sameDeliveryDate = possDeliveryDates.dates.find(
            (date: { date: string, price: number }) => (queryParamDate.isSame(date.date, "day")),
          )?.date;
          if (!sameDeliveryDate) {
            const closestDeliveryDate = possDeliveryDates.dates.find(
              (date: { date: string, price: number }) => (queryParamDate < dayjs(date.date) && date.price === 0),
            )?.date;
            if (!closestDeliveryDate) {
              const nextAvailableTrialDate = possDeliveryDates?.dates?.find(
                (date: { date: string, price: number }) => date.price === 0,
              )?.date;
              if (nextAvailableTrialDate) {
                setDeliveryDate(nextAvailableTrialDate);
              }
            } else {
              setDeliveryDate(closestDeliveryDate);
            }
          } else {
            setDeliveryDate(sameDeliveryDate);
          }
        } else {
          const possDeliveryDates = deliveryDatesResponse.responseBody;
          const nextAvailableTrialDate = possDeliveryDates.dates.find(
            (date: { date: string, price: number }) => date.price === 0,
          )?.date;
          if (nextAvailableTrialDate) {
            setDeliveryDate(nextAvailableTrialDate);
          }
          setPossibleDeliveryDates(possDeliveryDates.dates);
        }
      } else {
        console.error("Couldn't find delivery date", deliveryDatesResponse);
      }
    } catch (error) {
      console.error("Couldn't find delivery date", error);
    }
  };

  useEffect(() => {
    void getNextAvailableDeliveryDate();
  }, [ searchParams ]);

  const getBuyButtonText = () => {
    if (product === "PLANET_SCOOP_LITTER") {
      return "Get litter with 50% off";
    }
    return "Buy now";
  };

  return (
    <Section component={SectionOneClickPurchaseWithDate.name} size={ { top: "md-lg", bottom: "md-lg" } } theme={theme}>
      <Container>
        { title && (
          <>
            <TextSubtitle tag="h2" size={1} align="center">
              { title }
            </TextSubtitle>
            <Spacer size="md-lg" />
          </>
        ) }
        <Grid>
          <Column data-testid="card" spans={{ md: 6, "2xl": 4 }} offset={{ md: 4, "2xl": 5 }} className="bg-white">
            <div className="relative z-20">
              <ImageCarousel images={card.images} />
              { card.tag && (
                <SimpleTag bgColor="pink" className="absolute top-4 left-4 z-40" whitespace="preWrap" label={card.tag}></SimpleTag>
              ) }
            </div>
            <div className="p-4">
              <Grid gap="lg">
                <Column>
                  <TextSubtitle size={3} align="center" data-testid="cardTitle">
                    { card.title?.toUpperCase() }
                  </TextSubtitle>
                  { card.icons && (
                    <>
                      <Spacer size="md" />
                      <Grid>
                        { card.icons.map((icon) => (
                          <Column key={icon.title} spans={3} align="center">
                            <div className="max-w-[50px]">
                              <Image image={icon.image} alt={icon.title} />
                            </div>
                            <Spacer size="sm" />
                            <TextBody align="center" weight="bold" size={4}>{ icon.title }</TextBody>
                          </Column>
                        )) }
                      </Grid>
                    </>
                  ) }
                  <Spacer size="lg" />
                  <div className="flex justify-center items-center">
                    <TextBody size={3} align="center" className="flex justify-center items-center">
                      <span data-testid="cardPricePrefix">
                        { card.pricePrefix }
                      </span>
                      { card.price && (
                        <>
                          &nbsp;{ card.salePrice ? (<s data-testid="cardPrice">{ card.price }</s>) : card.price }
                        </>
                      ) }
                      { card.salePrice && (
                        <>
                          &nbsp;<TextSubtitle tag="span" size={3} data-testid="cardSalePrice">{ card.salePrice }</TextSubtitle>
                        </>
                      ) }
                    </TextBody>
                    { card.tag && (
                      <SimpleTag bgColor="pink" className="max-w-[75px] ml-2" whitespace="preWrap" label={ card.tag }></SimpleTag>
                    ) }
                  </div>
                  <Spacer size="sm" />
                  <Select name="deliveryDate" label="Select delivery date" onChange={(event) => setDeliveryDate(event.target.value)} selected={deliveryDate ?? undefined} options={possibleDeliveryDates.map((opt) => ({
                    name: dayjs(opt.date).format("ddd D MMMM YYYY"),
                    value: opt.date,
                    selected: opt.date === deliveryDate,
                  }))}></Select>
                  <Spacer size="lg" />
                  <Column align="center" data-testid="cardAddProductWrapper">
                    <Button onClick={checkoutWithProductAndContinue} state={checkoutState} disabled={!deliveryDate} data-testid="continueWithOneClickPurchase">{ getBuyButtonText() }</Button>
                  </Column>
                  <Spacer size="lg" />
                  <Spacer size="md" />
                  <TextBody size={4} align="center" data-testid="cardFooter">
                      Receive <b>1 bag every 4 weeks</b> after trial. Change quantity, frequency or cancel
                      from <a className="underline font-bold" href="/forms/scoop-planet-existing-user">here</a> or anytime from your account. No additional delivery charge applied.
                  </TextBody>
                  <Spacer size="md" />
                  <Column align="center">
                    <Button design="underline" data-testid="cardContinueWithoutOneClickPurchase" onClick={continueWithoutProduct}>
                        No Thanks
                    </Button>
                  </Column>
                </Column>
              </Grid>
            </div>
          </Column>
        </Grid>
      </Container>
    </Section>
  );
};

export default SectionOneClickPurchaseWithDate;
